/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useElementSize } from '../../hooks/useElementSize';

import type { CustomDisclaimerProps } from './components/CustomDisclaimer';
import { FooterAIBrand } from './components/FooterAIBrand';
import { FooterDisclaimer } from './components/FooterDisclaimer';
import { AI_BREAKPOINTS, ROVO_BREAKPOINTS } from './constants';
import messages from './messages';
import { type AIBrand } from './types';

const footerStyles = cssMap({
	default: {
		display: 'flex',
		flexWrap: 'wrap',
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		gap: token('space.050'),
		borderTopStyle: 'solid',
		alignItems: 'center',
		justifyContent: 'space-between',
		borderTopColor: token('color.border'),
		borderTopWidth: token('border.width'),
	},
});

interface FooterProps {
	/** The brand to display */
	brand: AIBrand;
	/**
	 * Custom disclaimer to be displayed in the footer.
	 *
	 */
	disclaimer: CustomDisclaimerProps['disclaimer'] | undefined;
	/** The feedback thumbs up and down components. */
	feedbackForm?: JSX.Element;

	/** Whether to show beta lozenge or not. */
	showBetaLozenge?: boolean;

	/**
	 * Whether to show the disclaimer or not.
	 *
	 * Some pages, for example, `loading` screen should show only AI brand logo without disclaimer.
	 */
	showDisclaimer: boolean;
	/** Whether to show feedback thumbs up and down or not. */
	showFeedback?: boolean;
}

export function Footer({
	brand,
	showDisclaimer,
	disclaimer,
	showBetaLozenge,
	showFeedback,
	feedbackForm,
}: FooterProps) {
	const { formatMessage } = useIntl();
	const [setElement, { width }] = useElementSize();

	const appearance = useMemo(() => {
		if (brand === 'atlassian-intelligence') {
			if (width < AI_BREAKPOINTS.medium) {
				return 'small' as const;
			}

			if (width < AI_BREAKPOINTS.large) {
				return 'medium' as const;
			}

			return 'large' as const;
		} else {
			if (width < ROVO_BREAKPOINTS.medium) {
				return 'small' as const;
			}

			if (width < ROVO_BREAKPOINTS.large) {
				return 'medium' as const;
			}

			return 'large' as const;
		}
	}, [width, brand]);

	return (
		<Box ref={setElement} xcss={footerStyles.default}>
			<Inline space="space.050">
				{showBetaLozenge && (
					<Lozenge testId="beta-lozenge">{formatMessage(messages.betaLabel)}</Lozenge>
				)}
				{showDisclaimer ? (
					<FooterDisclaimer disclaimer={disclaimer} appearance={appearance} />
				) : (
					<Box />
				)}
			</Inline>
			{!fg('ai-smart-button-team-25') && !fg('aifc_create_enabled') && !showFeedback && (
				<FooterAIBrand brand={brand} appearance={appearance} />
			)}
			{showFeedback && (
				<Inline space="space.050">
					<Inline>{formatMessage(messages.compactFeedbackText)}</Inline>
					<Inline>{feedbackForm}</Inline>
				</Inline>
			)}
		</Box>
	);
}
